// Boiler Plate Imports
import styled from "styled-components";
import colors from "../../theme/colors";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  remove as removeNotification,
  add as addNotification,
} from "../../redux-store/notificationsSlice";
import { update as setBreadCrumbs } from "../../redux-store/breadCrumbsSlice";
import { update as updateUserProfile } from "../../redux-store/userSlice";
import API from "../../api";

// Conatiner Component Imports
import { Button, Card } from "@mui/material";
import BreadCrumbs from "../../components/Navigation/breadcrumbs";
import ModalComponent from "../../components/Modal";
import TableHeader from "../../components/Page/tableHeader";
import TableSubHeader from "../../components/Page/tableSubHeader";

// Page Component Imports
import OrganizationLocationForm from "../../components/Forms/organizationLocationForm";
import { FormatPhoneNumber } from "../../components/PhoneNumberInput";
import { updateReloadOrganization } from "../../redux-store/uxSlice";
import { FormatAddressShort } from "../../components/Forms/shared/address";
import { useParams } from "react-router-dom";
import { ItemLink, TableComponent } from "../../components/Table/dataGridPro";

const ActiveAlarmsTagNone = styled.div`
  background: ${colors.grayLight};
  color: ${colors.grayDark};
  border: 1px solid ${colors.gray};
  border-radius: 15px;
  text-align: center;
  padding: 2px;
  width: 60px;
`;

const ActiveAlarmsTagSome = styled.div`
  background: ${colors.redLightError};
  color: ${colors.redError};
  border: 1px solid ${colors.redError};
  border-radius: 15px;
  text-align: center;
  padding: 2px;
  width: 60px;
  font-weight: bold;
`;

const Table = React.memo(TableComponent);

const OrganizationLocationsContainer = () => {
  const userProfile = useSelector((state) => state.user);
  const organization = useSelector((state) => state.organization);

  const [savingLocation, setSavingLocation] = useState(false);
  const [savingLocationError, setSavingLocationError] = useState(false);
  const [reloadData, setReloadData] = useState(false);

  const [openModal, setOpenModal] = useState(false);
  const dispatch = useDispatch();

  let { organizationId } = useParams();

  const tableColumns = useMemo(
    () => [
      {
        headerName: "Location Name",
        field: "name",
        valueGetter: (value, row) => {
          return row.name;
        },
        renderCell: ({ row }) => {
          return (
            <ItemLink to={`/${organizationId}/locations/${row.id}`}>
              {row.name}
            </ItemLink>
          );
        },
      },
      {
        headerName: "Active Alarms",
        field: "activeAlarmsCount",
        display: "flex",
        type: "number",
        valueGetter: (value, row) => {
          return row.activeAlarmsCount;
        },
        renderCell: ({ row }) => {
          if (row.activeAlarmsCount === 0) {
            return <ActiveAlarmsTagNone>none</ActiveAlarmsTagNone>;
          } else {
            return (
              <ActiveAlarmsTagSome>{row.activeAlarmsCount}</ActiveAlarmsTagSome>
            );
          }
        },
      },
      {
        headerName: "Tanks",
        field: "tanksCount",
        type: "number",
      },
      {
        headerName: "Address",
        field: "address",
      },
      {
        headerName: "Phone",
        field: "phoneString",
      },
      {
        headerName: "Date Created",
        field: "dateCreateObject",
        type: "dateTime",
      },
    ],
    [organizationId]
  );

  useEffect(() => {
    //Set Bread Crumbs
    dispatch(
      setBreadCrumbs([
        {
          title: organization.name,
          link: "/organization",
        },
        {
          title: "Locations",
          link: "/locations",
        },
      ])
    );
  }, [organization]);

  const dataLayer = {
    GetOrganizationLocations: async () => {
      const locations = await API.Location.LocationsGet({ organizationId });
      const locationRows = locations.map((location) => {
        return {
          ...location,
          status: (() => {
            if (location.deleted) {
              return "Deactivated";
            } else {
              return "Active";
            }
          })(),
          dateCreateObject: new Date(location.created),
          phoneString: FormatPhoneNumber(location.phone),
          address: FormatAddressShort(location),
          activeAlarms: 0,
        };
      });

      locationRows.sort((a, b) => a.name.localeCompare(b.name));
      return locationRows;
    },
  };

  const handlers = {
    addLocationClick: () => {
      setSavingLocationError("");
      setOpenModal(true);
    },
    closeLocationModal: () => {
      if (!savingLocation) {
        setOpenModal(false);
      }
    },
    saveLocation: async (data) => {
      setSavingLocationError("");
      setSavingLocation(true);
      try {
        await API.Location.LocationCreate({
          createLocationData: { ...data, organizationId },
        });
        //Reload Table Data
        setReloadData(true);
        dispatch(updateReloadOrganization(true));
        //Create Success Notification
        setOpenModal(false);
        const id = new Date().getTime();
        dispatch(
          addNotification({
            id,
            severity: "success",
            title: "Success",
            message: "Location created successfully",
          })
        );
        setTimeout(() => {
          dispatch(removeNotification(id));
        }, 3000);
      } catch (err) {
        setSavingLocationError(err);
        console.log(err);
        const id = new Date().getTime();
        dispatch(
          addNotification({
            id,
            severity: "error",
            title: "Error",
            message: err,
          })
        );
        setTimeout(() => {
          dispatch(removeNotification(id));
        }, 3000);
      }
      setSavingLocation(false);
    },
  };

  return (
    <>
      
      <Card>
        <TableHeader>
          <div>
            <h2>Locations</h2>
            <TableSubHeader>
              Below is a list of all Locations within this Organization
            </TableSubHeader>
          </div>

          {userProfile?.role && userProfile.role !== "TECH" && (
            <Button
              onClick={handlers.addLocationClick}
              variant="contained"
              color="primary"
              size="large"
            >
              ADD LOCATION
            </Button>
          )}
        </TableHeader>
        <Table
          columns={tableColumns}
          autoResizeColumns={false}
          label={""}
          isAdminView={false}
          reloadData={reloadData}
          placeholder={"There are no location created yet"}
          getRowsFunction={dataLayer.GetOrganizationLocations}
          initialState={{
            pinnedColumns: { left: ["name"] },
            sorting: {
              sortModel: [
                { field: "activeAlarmsCount", sort: "desc" },
                { field: "name", sort: "asc" },
              ],
            },
          }}
        />
      </Card>
      <ModalComponent headerText="Add Location" open={openModal}>
        <OrganizationLocationForm
          handleSave={handlers.saveLocation}
          handleCancel={handlers.closeLocationModal}
          loading={savingLocation}
          error={savingLocationError}
        />
      </ModalComponent>
    </>
  );
};

export default OrganizationLocationsContainer;
